import { useEffect, useRef, useState } from 'react';

/**
 * Toast — the app's one confirmation strip, ported from views/layout.ejs.
 *
 * The page's markup is `<div class="toast" id="toast"></div>` after `</main>`, and its script shows a message by
 * setting the text, adding `.show` and removing it 2.5s later; `.toast.show` is what app.css reveals. A save that
 * lands on `?saved=1` says "✓ Saved" the same way, which is why every EJS save ends in a redirect.
 *
 * The SPA had no toast at all, so an in-place save (the logo animation, the theme, the basis method) closed with
 * nothing on screen: the write landed, the form looked exactly as it had, and a reader reports "clicking save does
 * nothing". `showToast` is the one way to say a save happened.
 */
const EVENT = 'pf:toast';

/** Show the confirmation strip. The text is the message, as the layout's script takes it. */
export function showToast(text: string) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: text }));
}

export function ToastHost() {
  const [text, setText] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onToast = (e: Event) => {
      const msg = (e as CustomEvent<string>).detail || '';
      if (!msg) return;
      setText(msg);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setText(''), 2500);
    };
    window.addEventListener(EVENT, onToast);
    return () => {
      window.removeEventListener(EVENT, onToast);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // The `.show` class is what app.css reveals; the text stays in the node for the 2.5s it is up.
  return <div className={text ? 'toast show' : 'toast'} id="toast">{text}</div>;
}
